import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SharedPageLayout from '../components/SharedPageLayout';
import { Mail, Phone, MapPin, Upload, FileText, Zap, Settings, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { trackContactFormSubmission } from '../utils/analytics';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;


export const ServicesRoute = () => {
  const navigate = useNavigate();
  
  // Handle hash-based navigation when component mounts or hash changes
  React.useEffect(() => {
    const hash = window.location.hash.substring(1); // Remove the # symbol
    if (hash) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Get element position and scroll with offset to show title
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100; // 100px offset to show title and some space above
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  
  return (
    <SharedPageLayout currentPage="services">
      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">Professional Services</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Our Services
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Engineering-driven 3D printing solutions backed by 8+ years of experience
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Core Services with Image */}
        <section className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> is your one-stop destination for all things 3D printing, leveraging our team's extensive engineering background to deliver tailored solutions that bridge ideas and execution. Whether you're a hobbyist, entrepreneur, or enterprise, our comprehensive offerings ensure you have the tools, expertise, and support to succeed.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                From initial concept to final production, our comprehensive services cover every stage of the 3D printing workflow. We combine cutting-edge technology with engineering expertise to deliver results that exceed expectations.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img 
                src="/services_illustration_2.png" 
                alt="Precision 3D Printing Workspace"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* 3D File Printing Card */}
            <div id="3d-printing" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Upload className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">3D File Printing</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Professional analysis of your CAD files with material recommendations tailored to your specific use case and performance requirements.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Comprehensive file analysis and validation for optimal print quality</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Expert material selection based on mechanical properties and application requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Post-processing options including smoothing, painting, and finishing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Fast turnaround times with production time estimates based on complexity</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 2D to 3D Conversion Card */}
            <div id="conversion" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">2D to 3D Conversion</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Expert engineering services to transform technical drawings into optimized 3D models ready for production with proper tolerances.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Accurate conversion from 2D drawings, blueprints, and technical sketches</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Engineering-grade CAD modeling with proper dimensions and tolerances</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Design for manufacturability (DFM) optimization for 3D printing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Multiple file format support (STEP, STL, OBJ, and more)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Design Services Card */}
            <div id="design" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Design Services</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Custom product design from concept sketches or verbal descriptions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Functional prototyping and iterative design refinement</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Engineering analysis including stress testing and load calculations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Production-ready files optimized for 3D printing technology</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Product Optimization Card */}
            <div id="optimization" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Settings className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Product Optimization</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Engineering analysis and optimization for maximum performance, durability, and manufacturing efficiency.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Weight reduction through topology optimization and lattice structures</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Material efficiency analysis to reduce costs while maintaining performance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Structural reinforcement for load-bearing and high-stress applications</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Print orientation and support structure optimization</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Realistic Renders Card - NEW */}
            <div id="renders" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Eye className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Model & Product Renders</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Photorealistic visualization services to showcase your product designs before manufacturing, perfect for presentations and marketing.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>High-quality photorealistic renders with accurate materials and lighting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Multiple viewing angles and environmental settings for comprehensive visualization</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Product animation and 360-degree rotation videos available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Marketing-ready images for presentations, websites, and promotional materials</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section with Visual Showcase */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">A Ton of Experience. Literally.</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our engineering team has hands-on experience printing over 3,000+ pounds of material 
              across every possible use case and application type.
            </p>
          </div>

          {/* Featured Image Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/images/services-prototyping.jpg" 
                alt="Prototype Testing"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/images/services-quality.jpg" 
                alt="3D Printed Parts"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/services_functional_picture_3.jpg" 
                alt="Precision Mechanical Components"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 bg-slate-50 rounded-3xl p-12">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Prototype</div>
              <p className="text-slate-700 mb-4">
                Rapid iteration and testing phases for product development, functional validation, 
                and design verification with quick turnaround times.
              </p>
              <div className="text-sm text-slate-500">Applications: Concept validation, fit testing, form studies</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-green-600 mb-2">Cosmetic</div>
              <p className="text-slate-700 mb-4">
                Display-quality parts with smoothed surfaces, professional finishes, 
                painting preparation, and presentation-ready aesthetics.
              </p>
              <div className="text-sm text-slate-500">Applications: Trade shows, marketing materials, visual prototypes</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-purple-600 mb-2">Functional</div>
              <p className="text-slate-700 mb-4">
                Working components that integrate into assemblies, mechanisms, 
                and systems with proper dimensional accuracy and material properties.
              </p>
              <div className="text-sm text-slate-500">Applications: Jigs, fixtures, brackets, housings</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-red-600 mb-2">Load Bearing</div>
              <p className="text-slate-700 mb-4">
                Structural components designed to handle mechanical stress, 
                weight, and operational forces with engineering validation.
              </p>
              <div className="text-sm text-slate-500">Applications: Brackets, supports, structural elements</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-orange-600 mb-2">Post-Processed</div>
              <p className="text-slate-700 mb-4">
                Smoothed, sanded, and finished parts ready for painting, 
                coating, or wrapping applications with professional surface quality.
              </p>
              <div className="text-sm text-slate-500">Applications: Finished products, painted parts, wrapped components</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-teal-600 mb-2">Production</div>
              <p className="text-slate-700 mb-4">
                End-use parts for manufacturing, tooling, and production applications 
                with consistent quality and dimensional accuracy.
              </p>
              <div className="text-sm text-slate-500">Applications: End-use parts, tooling, manufacturing aids</div>
            </div>
          </div>
        </section>

        {/* Why Choose Us with Image */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Why Choose <span className="brand-name"><span className="brand-name-semibold">CREATE 3D</span> <span className="brand-name-bold">Works</span></span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Engineering expertise meets manufacturing flexibility for results you can trust.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/what-drives-us.png" 
                  alt="Our Mission"
                  className="rounded-2xl w-full h-auto shadow-xl"
                />
              </div>
            </div>
            <div className="space-y-8 order-1 md:order-2">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Engineering-First Approach</h3>
                <p className="text-slate-700">
                  Created by engineers who understand the technical challenges of 3D printing. 
                  We don't just print - we analyze, optimize, and engineer solutions.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Proven Track Record</h3>
                <p className="text-slate-700">
                  Over 3,000 lb. of material printed across every conceivable use case. 
                  From delicate prototypes to heavy-duty production components.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Complete Flexibility</h3>
                <p className="text-slate-700">
                  Whether you need rapid prototypes, cosmetic displays, functional parts, 
                  or production components - we have the experience to deliver.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Material Expertise</h3>
                <p className="text-slate-700">
                  Deep knowledge of material properties, applications, and limitations. 
                  We recommend the right material for your specific requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get an instant quote and see why engineers choose <span className="brand-name"><span className="brand-name-semibold">CREATE 3D</span> <span className="brand-name-bold">Works</span></span> for their most challenging projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const quoteSection = document.querySelector('#instant-quote');
                  if (quoteSection) {
                    quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Instant Quote
            </button>
            <button 
              onClick={() => navigate('/products')}
              className="border border-blue-200 text-white hover:bg-blue-500 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Browse Products
            </button>
          </div>
        </section>

      </div>
    </SharedPageLayout>
  );
};


export const ContactRoute = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_BASE = BACKEND_URL;
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you for contacting us! We will get back to you within 24 hours.' });
        
        // Track contact form submission in Google Analytics
        trackContactFormSubmission('Contact Page Form');
        
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or email us directly.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or email us directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedPageLayout currentPage="contact">
      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">Get In Touch</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Contact Us
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Get in touch with our engineering team—we're here to help bring your project to life
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
              <p className="text-slate-600 text-lg">hello@create3dworks.com</p>
              <p className="text-slate-500 text-sm mt-1">Response within 2 hours</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Phone className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Call Us</h3>
              <p className="text-slate-600 text-lg">+1 (555) 123-4567</p>
              <p className="text-slate-500 text-sm mt-1">Mon-Fri 9AM-6PM EST</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h3>
              <p className="text-slate-600 text-lg">590 Portland St - Unit 116<br />Dartmouth, NS B2W 6B7<br />Canada</p>
              <p className="text-slate-500 text-sm mt-1">Open Mon-Fri 9AM-5PM</p>
            </CardContent>
          </Card>
        </div>

        {/* Professional Message Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 text-center border border-blue-100">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">We're Here to Help</h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Whether you have questions about our services, need technical guidance, or want to discuss 
              your project requirements, our engineering team is ready to provide expert support.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="shadow-xl border-0 rounded-2xl">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-t-2xl">
              <CardTitle className="text-3xl font-bold text-slate-900">Send Us a Message</CardTitle>
              <p className="text-slate-600 mt-3 text-lg">Fill out the form below and we'll get back to you within 2 hours during business hours.</p>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    type="text"
                    placeholder="What is this regarding?"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <textarea
                    id="message"
                    rows="6"
                    placeholder="Tell us more about your project or inquiry..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {submitStatus && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#0007b5] to-[#000690] hover:from-blue-700 hover:to-blue-800 text-white py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Trust Elements */}
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-[#0007b5] font-bold text-3xl mb-2">2 Hours</div>
              <div className="text-slate-600">Average Response Time</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-green-600 font-bold text-3xl mb-2">24/7</div>
              <div className="text-slate-600">Email Support Available</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-purple-600 font-bold text-3xl mb-2">100%</div>
              <div className="text-slate-600">Satisfaction Guaranteed</div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="max-w-4xl mx-auto mt-16 bg-slate-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Prefer Other Contact Methods?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-[#0007b5]" />
                Direct Email
              </h3>
              <p className="text-slate-700 mb-2">
                For detailed technical inquiries or file attachments:
              </p>
              <a href="mailto:hello@create3dworks.com" className="text-[#0007b5] hover:text-[#000690] font-medium">
                hello@create3dworks.com
              </a>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-[#0007b5]" />
                Phone Support
              </h3>
              <p className="text-slate-700 mb-2">
                Call us during business hours:
              </p>
              <a href="tel:+15551234567" className="text-[#0007b5] hover:text-[#000690] font-medium">
                +1 (555) 123-4567
              </a>
              <p className="text-sm text-slate-500 mt-1">Mon-Fri 9AM-6PM EST</p>
            </div>
          </div>
        </div>

      </div>
    </SharedPageLayout>
  );
};
